<template>
  <!--translate-->
  <div class="email-settings-page">
    <v-card class="mx-auto my-4 mb-8" max-width="800" flat outlined>
      <v-container class="mx-4 my-4">
        <v-row class="mb-3">
          <h3>{{ $t("settings.emailSettings.title") }}</h3>
          <div class="flex-grow-1"></div>
        </v-row>
      </v-container>
      <v-form class="pa-4 pt-0" ref="form" lazy-validation>
        <v-expansion-panels>
          <v-expansion-panel
            v-for="(text, textIndex) in texts"
            v-bind:key="textIndex"
            cols="12"
            sm="12"
            md="12"
          >
            <v-expansion-panel-header>
              <h4>{{ text.title }}</h4>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div v-for="(item, itemIndex) of text.items" :key="itemIndex">
                <v-divider v-if="itemIndex !== 0" class="my-4"></v-divider>
                <div>
                  <div v-if="item.label" class="text-h5">
                    {{ item.label }}
                  </div>
                  <v-row>
                    <template
                      v-if="item.placeholders?.length && !isReadOnlyUser"
                    >
                      <v-col cols="12" md="3" class="order-md-3 mt-4 mt-md-0">
                        <div class="text-h6">
                          {{ $t("settings.emailSettings.variables") }}
                        </div>
                        <div
                          class="placeholder-container d-flex flex-row flex-md-column"
                        >
                          <div
                            v-for="(placeholder, index) of item.placeholders"
                            :key="index"
                            class="placeholder-item"
                          >
                            <v-tooltip top>
                              <template v-slot:activator="{ on }">
                                <div
                                  v-on="on"
                                  @click.prevent="
                                    placeholderClick(
                                      item.type,
                                      textIndex,
                                      itemIndex,
                                      `{{${placeholder.key}}}`
                                    )
                                  "
                                >
                                  <span>&#123;</span><span>&#123;</span
                                  >{{ placeholder.key }}<span>&#125;</span
                                  ><span>&#125;</span>
                                </div>
                              </template>
                              <span>{{ placeholder.label }}</span>
                            </v-tooltip>
                          </div>
                        </div>
                      </v-col>
                      <v-col cols="auto" class="order-2 d-none d-md-block">
                        <v-divider vertical></v-divider> </v-col
                    ></template>
                    <v-col cols="12" md="8" class="order-md-1">
                      <v-text-field
                        v-if="item.type === 'text'"
                        :ref="`textField${textIndex}.${itemIndex}`"
                        v-model="item.value"
                        :label="item.label"
                        :disabled="isReadOnlyUser"
                      ></v-text-field>
                      <quill-editor
                        v-else-if="item.type === 'quill'"
                        :ref="`quillEditor${textIndex}.${itemIndex}`"
                        class="largeTextField mb-6 ma-2"
                        data-cy="action"
                        v-model="item.value"
                        :options="editorOptions"
                      ></quill-editor>
                    </v-col>
                  </v-row>
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-form>
      <v-card-actions>
        <div class="flex-grow-1"></div>
        <v-btn
          v-if="!isReadOnlyUser"
          color="primary"
          outlined
          @click="saveEmailSettings"
          >{{ $t("general.save") }}</v-btn
        >
      </v-card-actions>
    </v-card>

    <emailNotificationSettings />
  </div>
</template>

<script>
import i18n from "../../i18n";
import emailNotificationSettings from "./emailNotificationSettings.vue";
export default {
  components: { emailNotificationSettings },
  data: () => ({
    texts: [],
    loadedValues: {},
  }),
  created() {
    this.initialize();
  },
  computed: {
    editorOptions() {
      return {
        placeholder: i18n.t("settings.email.placeholder"),
        readOnly: this.isReadOnlyUser,
        modules: {
          toolbar: this.isReadOnlyUser ? false : true,
        },
      };
    },
  },
  watch: {
    "$i18n.locale"() {
      if (this.texts.length) {
        this.texts = this.buildTexts(this.currentValues());
      }
    },
  },
  methods: {
    initialize() {
      this.$store.dispatch("getSettingData", "emailTexts").then((item) => {
        this.loadedValues = { ...((item && item.value) || {}) };
        this.texts = this.buildTexts(item.value);
      });
    },
    currentValues() {
      const map = {};
      this.texts.forEach((t) =>
        t.items.forEach((i) => {
          map[i.key] = i.value;
        })
      );
      return map;
    },
    buildTexts(v) {
      return [
        {
          title: this.$t("settings.emailSettings.instructions"),
          items: [
            {
              type: "text",
              key: "new_managed_instruction_assignment_subject",
              label: this.$t("settings.emailSettings.subject"),
              value: v.new_managed_instruction_assignment_subject,
              placeholders: [
                {
                  key: "employeeName",
                  label: i18n.t("settings.emailSettings.recipientFirstName"),
                },
                {
                  key: "instructionName",
                  label: i18n.t("settings.emailSettings.instructionName"),
                },
              ],
            },
            {
              type: "quill",
              key: "new_managed_instruction_assignment",
              label: this.$t("settings.emailSettings.message"),
              value: v.new_managed_instruction_assignment,
              placeholders: [
                {
                  key: "employeeName",
                  label: i18n.t("settings.emailSettings.recipientFirstName"),
                },
                {
                  key: "instructionName",
                  label: i18n.t("settings.emailSettings.instructionName"),
                },
                {
                  key: "link",
                  label: i18n.t("settings.email.instructionLink"),
                },
                {
                  key: "qrCode",
                  label: i18n.t("settings.email.instructionQrCode"),
                },
              ],
            },
          ],
        },
        {
          title: this.$t("settings.emailSettings.reminders"),
          items: [
            {
              type: "text",
              key: "instruction_assignment_reminder_subject",
              label: this.$t("settings.emailSettings.subject"),
              value: v.instruction_assignment_reminder_subject,
              placeholders: [
                {
                  key: "employeeName",
                  label: i18n.t("settings.emailSettings.recipientFirstName"),
                },
                {
                  key: "instructionName",
                  label: i18n.t("settings.emailSettings.instructionName"),
                },
              ],
            },
            {
              type: "quill",
              key: "instruction_assignment_reminder",
              label: this.$t("settings.emailSettings.message"),
              value: v.instruction_assignment_reminder,
              placeholders: [
                {
                  key: "employeeName",
                  label: i18n.t("settings.emailSettings.recipientFirstName"),
                },
                {
                  key: "instructionName",
                  label: i18n.t("settings.emailSettings.instructionName"),
                },
                {
                  key: "link",
                  label: i18n.t("settings.email.instructionLink"),
                },
                {
                  key: "qrCode",
                  label: i18n.t("settings.email.instructionQrCode"),
                },
              ],
            },
          ],
        },
        {
          title: this.$t("settings.emailSettings.examinations"),
          items: [
            {
              type: "text",
              key: "new_examination_subject",
              label: this.$t("settings.emailSettings.subject"),
              value: v.new_examination_subject,
              placeholders: [
                {
                  key: "employeeName",
                  label: i18n.t("settings.emailSettings.recipientFirstName"),
                },
                {
                  key: "examinationName",
                  label: i18n.t("settings.emailSettings.examinationName"),
                },
              ],
            },
            {
              type: "quill",
              label: this.$t("settings.emailSettings.obligatoryExaminations"),
              key: "new_examination_obligatory",
              value: v.new_examination_obligatory,
              placeholders: [
                {
                  key: "employeeName",
                  label: i18n.t("settings.emailSettings.recipientFirstName"),
                },
                {
                  key: "examinationName",
                  label: i18n.t("settings.emailSettings.examinationName"),
                },
                {
                  key: "link",
                  label: i18n.t("settings.email.examinationLink"),
                },
              ],
            },
            {
              type: "quill",
              label: this.$t("settings.emailSettings.optionalExaminations"),
              key: "new_examination_optional",
              value: v.new_examination_optional,
              placeholders: [
                {
                  key: "employeeName",
                  label: i18n.t("settings.emailSettings.recipientFirstName"),
                },
                {
                  key: "examinationName",
                  label: i18n.t("settings.emailSettings.examinationName"),
                },
              ],
            },
          ],
        },
        {
          title: this.$t("settings.emailSettings.incidentReport"),
          items: [
            {
              type: "text",
              key: "incident_report_subject",
              label: this.$t("settings.emailSettings.subject"),
              value: v.incident_report_subject,
              placeholders: [
                {
                  key: "name",
                  label: i18n.t("settings.emailSettings.incidentReportName"),
                },
                {
                  key: "incidentCourse",
                  label: i18n.t("settings.emailSettings.incidentCourse"),
                },
                {
                  key: "absence",
                  label: i18n.t("settings.emailSettings.absence"),
                },
              ],
            },
            {
              type: "quill",
              key: "incident_report",
              label: this.$t("settings.emailSettings.message"),
              value: v.incident_report,
              placeholders: [
                {
                  key: "name",
                  label: i18n.t("settings.emailSettings.incidentReportName"),
                },
                {
                  key: "incidentCourse",
                  label: i18n.t("settings.emailSettings.incidentCourse"),
                },
                {
                  key: "absence",
                  label: i18n.t("settings.emailSettings.absence"),
                },
              ],
            },
          ],
        },
        {
          title: this.$t("settings.emailSettings.medkitLog"),
          items: [
            {
              type: "text",
              key: "medkit_log_subject",
              label: this.$t("settings.emailSettings.subject"),
              value: v.medkit_log_subject,
              placeholders: [
                {
                  key: "name",
                  label: i18n.t("settings.emailSettings.medkitLogName"),
                },
                {
                  key: "incidentCourse",
                  label: i18n.t("settings.emailSettings.incidentCourse"),
                },
                {
                  key: "injuryType",
                  label: i18n.t("settings.emailSettings.injuryType"),
                },
              ],
            },
            {
              type: "quill",
              key: "medkit_log",
              label: this.$t("settings.emailSettings.message"),
              value: v.medkit_log,
              placeholders: [
                {
                  key: "name",
                  label: i18n.t("settings.emailSettings.medkitLogName"),
                },
                {
                  key: "incidentCourse",
                  label: i18n.t("settings.emailSettings.incidentCourse"),
                },
                {
                  key: "injuryType",
                  label: i18n.t("settings.emailSettings.injuryType"),
                },
              ],
            },
          ],
        },
        {
          title: this.$t("settings.emailSettings.proofReminder"),
          items: [
            {
              type: "text",
              key: "proof_reminder_subject",
              label: this.$t("settings.emailSettings.subject"),
              value: v.proof_reminder_subject,
              placeholders: [
                {
                  key: "proofCount",
                  label: i18n.t("settings.email.dueProofCount"),
                },
              ],
            },
            {
              type: "quill",
              key: "proof_reminder",
              label: this.$t("settings.emailSettings.message"),
              value: v.proof_reminder,
              placeholders: [
                {
                  key: "recipientName",
                  label: i18n.t("settings.email.recipientName"),
                },
                {
                  key: "proofCount",
                  label: i18n.t("settings.email.dueProofCount"),
                },
                {
                  key: "proofListDe",
                  label: i18n.t("settings.email.dueProofListDe"),
                },
                {
                  key: "proofListEn",
                  label: i18n.t("settings.emailSettings.overdueProofListEn"),
                },
              ],
            },
          ],
        },
        {
          title: this.$t("settings.emailSettings.ticketShare"),
          items: [
            {
              type: "text",
              key: "ticket_share_subject",
              label: this.$t("settings.emailSettings.subject"),
              value: v.ticket_share_subject,
              placeholders: [
                {
                  key: "title",
                  label: i18n.t("settings.emailSettings.ticketTitle"),
                },
                {
                  key: "senderName",
                  label: i18n.t("settings.emailSettings.senderName"),
                },
              ],
            },
            {
              type: "quill",
              key: "ticket_share",
              label: this.$t("settings.emailSettings.message"),
              value: v.ticket_share,
              placeholders: [
                {
                  key: "senderName",
                  label: i18n.t("settings.emailSettings.senderName"),
                },
                {
                  key: "title",
                  label: i18n.t("settings.emailSettings.ticketTitle"),
                },
                {
                  key: "description",
                  label: i18n.t("settings.emailSettings.ticketDescription"),
                },
                {
                  key: "priority",
                  label: i18n.t("settings.emailSettings.ticketPriority"),
                },
                {
                  key: "dueDate",
                  label: i18n.t("settings.emailSettings.ticketDueDate"),
                },
              ],
            },
          ],
        },
        {
          title: this.$t("settings.emailSettings.ticketAssigned"),
          items: [
            {
              type: "text",
              key: "ticket_assigned_subject",
              label: this.$t("settings.emailSettings.subject"),
              value: v.ticket_assigned_subject,
              placeholders: [
                {
                  key: "title",
                  label: i18n.t("settings.emailSettings.ticketTitle"),
                },
              ],
            },
            {
              type: "quill",
              key: "ticket_assigned",
              label: this.$t("settings.emailSettings.message"),
              value: v.ticket_assigned,
              placeholders: [
                {
                  key: "recipientName",
                  label: i18n.t("settings.emailSettings.recipientFirstName"),
                },
                {
                  key: "title",
                  label: i18n.t("settings.emailSettings.ticketTitle"),
                },
                {
                  key: "description",
                  label: i18n.t("settings.emailSettings.ticketDescription"),
                },
                {
                  key: "priority",
                  label: i18n.t("settings.emailSettings.ticketPriority"),
                },
                {
                  key: "dueDate",
                  label: i18n.t("settings.emailSettings.ticketDueDate"),
                },
              ],
            },
          ],
        },
        {
          title: this.$t("settings.emailSettings.riskAssessmentProofRequest"),
          items: [
            {
              type: "text",
              key: "risk_assessment_proof_request_subject",
              label: this.$t("settings.emailSettings.subject"),
              value: v.risk_assessment_proof_request_subject,
              placeholders: [
                {
                  key: "riskAssessmentTitle",
                  label: i18n.t("settings.emailSettings.riskAssessmentTitle"),
                },
                {
                  key: "version",
                  label: i18n.t("settings.emailSettings.riskAssessmentVersion"),
                },
              ],
            },
            {
              type: "quill",
              key: "risk_assessment_proof_request",
              label: this.$t("settings.emailSettings.message"),
              value: v.risk_assessment_proof_request,
              placeholders: [
                {
                  key: "recipientName",
                  label: i18n.t("settings.emailSettings.recipientFirstName"),
                },
                {
                  key: "riskAssessmentTitle",
                  label: i18n.t("settings.emailSettings.riskAssessmentTitle"),
                },
                {
                  key: "location",
                  label: i18n.t(
                    "settings.emailSettings.riskAssessmentLocation"
                  ),
                },
                {
                  key: "version",
                  label: i18n.t("settings.emailSettings.riskAssessmentVersion"),
                },
                {
                  key: "proofers",
                  label: i18n.t(
                    "settings.emailSettings.riskAssessmentProofers"
                  ),
                },
                {
                  key: "link",
                  label: i18n.t("settings.email.instructionLink"),
                },
              ],
            },
          ],
        },
        {
          title: this.$t("settings.emailSettings.riskAssessmentProofOverdue"),
          items: [
            {
              type: "text",
              key: "risk_assessment_proof_overdue_subject",
              label: this.$t("settings.emailSettings.subject"),
              value: v.risk_assessment_proof_overdue_subject,
              placeholders: [
                {
                  key: "riskAssessmentTitle",
                  label: i18n.t("settings.emailSettings.riskAssessmentTitle"),
                },
              ],
            },
            {
              type: "quill",
              key: "risk_assessment_proof_overdue",
              label: this.$t("settings.emailSettings.message"),
              value: v.risk_assessment_proof_overdue,
              placeholders: [
                {
                  key: "recipientName",
                  label: i18n.t("settings.emailSettings.recipientFirstName"),
                },
                {
                  key: "riskAssessmentTitle",
                  label: i18n.t("settings.emailSettings.riskAssessmentTitle"),
                },
                {
                  key: "location",
                  label: i18n.t(
                    "settings.emailSettings.riskAssessmentLocation"
                  ),
                },
                {
                  key: "version",
                  label: i18n.t("settings.emailSettings.riskAssessmentVersion"),
                },
                {
                  key: "lastProofedAt",
                  label: i18n.t(
                    "settings.emailSettings.riskAssessmentLastProofedAt"
                  ),
                },
                {
                  key: "link",
                  label: i18n.t("settings.email.instructionLink"),
                },
              ],
            },
          ],
        },
      ];
    },
    placeholderClick(type, textIndex, itemIndex, insertText) {
      if (this.isReadOnlyUser) return;
      if (type === "text") {
        this.insertTextIntoTextField(textIndex, itemIndex, insertText);
      } else if (type === "quill") {
        this.insertTextAtCursor(textIndex, itemIndex, insertText);
      }
    },
    async copyText(text) {
      if (
        navigator.clipboard &&
        typeof navigator.clipboard.writeText === "function"
      ) {
        await navigator.clipboard.writeText(text);
        this.$store.dispatch("triggerSnackbar", this.$t("general.copied"));
      } else {
        console.warn(
          "Clipboard API wird von diesem Browser nicht unterstützt. Fallback wird verwendet."
        );
        this.copyToClipboardFallback(text);
      }
    },
    copyToClipboardFallback(text) {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed"; // Verhindert das Scrollen
      textarea.style.opacity = "0"; // Unsichtbar machen
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand("copy");
        console.log("Text wurde kopiert!");
      } catch (err) {
        console.error("Fehler beim Kopieren in die Zwischenablage:", err);
      }
      document.body.removeChild(textarea);
    },

    insertTextAtCursor(textIndex, itemIndex, insertText) {
      const editorRef = this.$refs[`quillEditor${textIndex}.${itemIndex}`][0];
      const editor = editorRef.quill;

      // Explicitly focus the editor
      editor.focus();

      // Delay getting the selection slightly to ensure focus takes effect
      this.$nextTick(() => {
        const cursorPosition = editor.getSelection()?.index || 0;
        editor.insertText(cursorPosition, insertText);
        editor.setSelection(cursorPosition + insertText.length);
      });
    },
    saveEmailSettings() {
      //set edited data
      //new_instruction_assignment
      const settings = this.texts.reduce(
        (acc, curr) => ({
          ...acc,
          ...curr.items.reduce(
            (acc2, curr2) => ({ ...acc2, [curr2.key]: curr2.value ?? "" }),
            {}
          ),
        }),
        {}
      );
      this.$store
        .dispatch("setSettingData", {
          id: "emailTexts",
          value: { ...(this.loadedValues || {}), ...settings },
        })
        .finally(() => this.initialize());
    },
    insertTextIntoTextField(textIndex, itemIndex, insertText) {
      const fieldRef = this.$refs[`textField${textIndex}.${itemIndex}`][0];
      const input = fieldRef.$el.querySelector("input");

      const currentValue = this.texts[textIndex].items[itemIndex].value || "";

      // if cursor position is available, insert there
      const start = input.selectionStart ?? currentValue.length;
      const end = input.selectionEnd ?? currentValue.length;

      const newValue =
        currentValue.slice(0, start) + insertText + currentValue.slice(end);

      this.texts[textIndex].items[itemIndex].value = newValue;

      this.$nextTick(() => {
        input.focus();
        const newCursorPos = start + insertText.length;
        input.setSelectionRange(newCursorPos, newCursorPos);
      });
    },
  },
};
</script>
<style scoped>
.email-settings-page {
  padding-bottom: 120px;
}
.largeTextField > .ql-container {
  min-height: 120px !important;
  font-size: 16px !important;
}

.largeTextField > .ql-toolbar {
  font-size: 16px !important;
}
.required-error {
  font-size: 12px;
  line-height: 16px;
}
.placeholder-container {
  gap: 8px;

  .placeholder-item {
    width: fit-content;
    cursor: pointer;
    background: rgb(230, 230, 230);
    padding: 2px 4px;
    border-radius: 10px;
  }
}
</style>
